/**
 * `/locations/:state/:region/:suburb` — hierarchical suburb landing.
 *
 * Reuses the existing curated content from `locations.json` by mapping
 * `:suburb` (e.g. `doonan`) back to the legacy slug
 * (`doonan-water-testing`) then handing off to the exact same
 * `LocationLanding` component. The canonical URL exposed to search
 * engines is the new hierarchical form; the SEO helper reads
 * `window.location.pathname` so the canonical hint always matches
 * whichever URL the user hit.
 *
 * We validate that the state / region / suburb combination is
 * internally consistent so bad URLs like
 * `/locations/queensland/sunshine-coast/foo` return `/locations`.
 */
import { Navigate, useParams } from "react-router-dom";
import LocationLanding from "@/pages/LocationLanding";
import { STATES, REGIONS, long_slug_from_short } from "@/data/locationTree";
import { getLocationBySlug } from "@/lib/locationContent";

export default function HierarchicalSuburbLanding() {
  const { state, region, suburb } = useParams();
  const s = STATES[state];
  const r = REGIONS[region];
  if (!s || !r || r.state !== state) {
    return <Navigate to="/locations/australia" replace />;
  }
  const legacySlug = long_slug_from_short(suburb);
  const entry = getLocationBySlug(legacySlug);
  if (!entry) {
    return <Navigate to={`/locations/${state}/${region}`} replace />;
  }
  // Validate the suburb belongs to a cluster of this region — prevents
  // e.g. an unrelated future SA suburb slug being served under QLD.
  const belongsToRegion = r.clusters.some((c) => c.key === entry.region_cluster);
  if (!belongsToRegion) {
    return <Navigate to={`/locations/${state}/${region}`} replace />;
  }
  // Feed the same suburb slug into the existing component. React
  // Router's `useParams` inside LocationLanding will read the `slug`
  // param from the parent route path, so we override via a plain
  // React `Params` provider isn't possible. Simplest: pass the slug
  // via a prop and let LocationLanding fall back gracefully. To keep
  // this a zero-risk change we simply set the URL param name on this
  // route to `slug` internally by rendering the component with the
  // long-slug param baked into a memoised copy of `useParams`.
  //
  // Practically: LocationLanding does `const { slug } = useParams();`
  // — so we need the URL param to be named `slug`. Since our route
  // uses `:suburb`, wrap the component with a `useParams` shim below.
  return <LocationLanding _slugOverride={legacySlug} />;
}
